<template>
    <div id="app">

        <!-- Record Detail Popup -->
        <div v-if="selectedRecord" class="simple-popup" @click.self="selectedRecord = null">
            <div class="popup-content wide-popup">
                <div class="popup-header">
                    <h3>Error Record — #{{ selectedRecord.ID }}</h3>
                    <button @click="selectedRecord = null" class="close-btn">×</button>
                </div>
                <div class="popup-body">
                    <div class="detail-sections">

                        <!-- Identity -->
                        <div class="detail-section">
                            <h4><span class="source-icon">🔖</span> Identity</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>Record ID:</strong> {{ selectedRecord.ID }}</div>
                                <div class="detail-row"><strong>Device ID:</strong> <code
                                        class="id-code">{{ selectedRecord.DeviceID || '—' }}</code></div>
                                <div class="detail-row"><strong>Account ID:</strong> {{ selectedRecord.AccountID != null
                                    ? selectedRecord.AccountID : '—' }}</div>
                                <div class="detail-row"><strong>Store ID:</strong> {{ selectedRecord.StoreID != null ?
                                    selectedRecord.StoreID : '—' }}</div>
                                <div class="detail-row"><strong>Store:</strong> {{ selectedRecord.Store || '—' }}</div>
                                <div class="detail-row"><strong>Create Date:</strong> {{
                                    formatDateTime(selectedRecord.CreateDate) }}</div>
                            </div>
                        </div>

                        <!-- Tank & Measurements -->
                        <div class="detail-section equipment-section">
                            <h4><span class="source-icon">🛢️</span> Tank &amp; Measurements</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>Tank Nr:</strong> {{ selectedRecord.TankNr != null ?
                                    selectedRecord.TankNr : '—' }}</div>
                                <div class="detail-row"><strong>Product Name:</strong> {{ selectedRecord.Prodname || '—'
                                    }}</div>
                                <div class="detail-row"><strong>Mm (Level):</strong> {{ formatFloat(selectedRecord.Mm)
                                    }}</div>
                                <div class="detail-row"><strong>Water Mm:</strong> {{
                                    formatFloat(selectedRecord.WaterMm) }}</div>
                                <div class="detail-row"><strong>Temp:</strong> {{ formatFloat(selectedRecord.Temp) }} °C
                                </div>
                            </div>
                        </div>

                        <!-- Volume -->
                        <div class="detail-section cost-section">
                            <h4><span class="source-icon">📊</span> Volume</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>Vol:</strong> {{ formatFloat(selectedRecord.Vol) }} L
                                </div>
                                <div class="detail-row"><strong>Vol TC:</strong> {{ formatFloat(selectedRecord.VolTC) }}
                                    L</div>
                                <div class="detail-row"><strong>Water Vol:</strong> {{
                                    formatFloat(selectedRecord.WaterVol) }} L</div>
                                <div class="detail-row"><strong>Weight:</strong> {{ formatFloat(selectedRecord.Weight)
                                    }} kg</div>
                                <div class="detail-row"><strong>Prd Ratio:</strong> {{
                                    formatFloat(selectedRecord.PrdRatio) }}</div>
                                <div class="detail-row"><strong>Water Ratio:</strong> {{
                                    formatFloat(selectedRecord.WaterRatio) }}</div>
                            </div>
                        </div>

                        <!-- Error Info -->
                        <div class="detail-section personnel-section">
                            <h4><span class="source-icon">⚠️</span> Error Info</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>Type ID:</strong> {{ selectedRecord.TypeID != null ?
                                    selectedRecord.TypeID : '—' }}</div>
                                <div class="detail-row"><strong>Sub Type ID:</strong> {{ selectedRecord.SubTypeID !=
                                    null ? selectedRecord.SubTypeID : '—' }}</div>
                                <div class="detail-row">
                                    <strong>Status:</strong>
                                    <span :class="['status-badge', 'st-' + (selectedRecord.Status || 0)]">
                                        {{ formatStatus(selectedRecord.Status) }}
                                    </span>
                                </div>
                                <div class="detail-row"><strong>Message:</strong> {{ selectedRecord.Message || '—' }}
                                </div>
                                <div class="detail-row"><strong>State Info:</strong> {{ selectedRecord.StateInfo || '—'
                                    }}</div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <!-- Stat Popup -->
        <div v-if="statPopupType" class="simple-popup" @click.self="statPopupType = null">
            <div class="popup-content stats-popup">
                <div class="popup-header">
                    <h3>{{ getStatPopupTitle() }}</h3>
                    <button @click="statPopupType = null" class="close-btn">×</button>
                </div>
                <div class="stats-popup-body">
                    <div class="stats-table-wrapper">
                        <table class="stats-data-table">
                            <thead>
                                <tr>
                                    <th>Create Date</th>
                                    <th>Device ID</th>
                                    <th>Store ID</th>
                                    <th>Tank Nr</th>
                                    <th>Product</th>
                                    <th>Status</th>
                                    <th>Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="getStatPopupRecords().length === 0">
                                    <td colspan="7" class="empty-state" style="text-align:center;padding:40px;">
                                        <div>No records found</div>
                                    </td>
                                </tr>
                                <tr v-for="rec in getStatPopupRecords()" :key="rec.ID" class="stats-table-row"
                                    @click="selectedRecord = rec; statPopupType = null;">
                                    <td>{{ formatDateTime(rec.CreateDate) }}</td>
                                    <td><code class="id-code">{{ rec.DeviceID || '—' }}</code></td>
                                    <td>{{ rec.StoreID != null ? rec.StoreID : '—' }}</td>
                                    <td>{{ rec.TankNr != null ? rec.TankNr : '—' }}</td>
                                    <td>{{ rec.Prodname || '—' }}</td>
                                    <td>
                                        <span :class="['status-badge', 'st-' + (rec.Status || 0)]">
                                            {{ formatStatus(rec.Status) }}
                                        </span>
                                    </td>
                                    <td class="desc-cell">{{ rec.Message || '—' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Header -->
        <div class="header-toolbar">
            <div class="header-content">
                <div class="logo-section">
                    <div class="logo-icon"><span>⚠️</span></div>
                    <h1 class="dashboard-title">Data Error Monitor</h1>
                </div>
                <div class="header-controls">
                    <div class="loader-controls">
                        <div class="loader-toggle">
                            <label class="toggle-label">
                                <input type="checkbox" v-model="autoRefreshEnabled" @change="handleAutoRefreshToggle"
                                    class="toggle-checkbox">
                                <span class="toggle-text">Auto Refresh</span>
                            </label>
                        </div>
                        <div v-if="autoRefreshEnabled" class="refresh-interval">
                            <label class="interval-label">
                                <span class="interval-text">Every:</span>
                                <select v-model="refreshInterval" @change="handleRefreshIntervalChange"
                                    class="interval-select">
                                    <option :value="30000">30 sec</option>
                                    <option :value="60000">1 min</option>
                                    <option :value="300000">5 min</option>
                                    <option :value="600000">10 min</option>
                                </select>
                            </label>
                        </div>
                        <button class="header-refresh-btn" @click="refreshData">
                            <span class="refresh-icon">🔄</span><span>Refresh</span>
                        </button>
                    </div>
                    <div class="live-indicator">
                        <div :class="['live-dot', { 'auto-refresh': autoRefreshEnabled }]"></div>
                        <span style="color:white;font-size:0.875rem;">
                            {{ autoRefreshEnabled ? 'Live (' + formatRefreshInterval() + ')' : 'Live' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">

            <!-- Stats -->
            <div class="stats-section">
                <div class="stats-header">
                    <h3 class="stats-title">
                        Error Overview
                        <span class="stats-filter-note">— showing filtered results</span>
                    </h3>
                    <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
                        <span class="toggle-icon">{{ statsVisible ? '📊' : '📈' }}</span>
                        {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
                    </button>
                </div>
                <transition name="slide-fade">
                    <div v-if="statsVisible" class="stats-wrapper">
                        <div class="stats-container">
                            <div class="stat-card clickable-stat total-stat" @click="openStatPopup('total')">
                                <div class="stat-label">Total Errors</div>
                                <div class="stat-value">{{ summaryStats.totalErrors }}</div>
                                <div class="stat-change positive"><span class="stat-icon">📝</span><span>Filtered
                                        records</span></div>
                                <div class="stat-hint">Click for details</div>
                            </div>
                            <div class="stat-card volume-stat">
                                <div class="stat-label">Unique Devices</div>
                                <div class="stat-value" style="color:#4CAF50;">{{ summaryStats.uniqueDevices }}</div>
                                <div class="stat-change"><span class="stat-icon">📡</span><span>Active devices</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Filters -->
            <div class="search-section">
                <div class="filter-header" @click="filtersVisible = !filtersVisible" style="cursor:pointer;">
                    <div class="filter-title-group">
                        <span class="filter-icon">🔍</span>
                        <h3 class="filter-title">Filters</h3>
                        <span v-if="activeFilterCount > 0" class="filter-count-badge">{{ activeFilterCount }}
                            active</span>
                    </div>
                    <div class="filter-header-buttons">
                        <button class="toggle-filter-btn">{{ filtersVisible ? '▲ Hide' : '▼ Show' }}</button>
                    </div>
                </div>
                <transition name="slide-fade">
                    <div v-if="filtersVisible" class="search-controls-wrapper">
                        <div class="search-controls">

                            <!-- Device filter -->
                            <div class="search-group">
                                <label class="search-label">Device ID</label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('devices')">
                                        <span class="selected-items">{{ filters.deviceIds.length === 0 ? 'All' :
                                            filters.deviceIds.length + ' selected' }}</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.devices" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllDevices"
                                                    :checked="filters.deviceIds.length === uniqueDevices.length">
                                                <span>{{ filters.deviceIds.length === uniqueDevices.length ? 'DeselectAll' : 'Select All' }}</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label v-for="d in uniqueDevices" :key="d.id" class="checkbox-option">
                                                <input type="checkbox" :value="d.id" v-model="filters.deviceIds"
                                                    @change="applyFilters">
                                                <span>{{ d.id }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Store filter -->
                            <div class="search-group">
                                <label class="search-label">Store ID</label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('stores')">
                                        <span class="selected-items">{{ filters.storeIds.length === 0 ? 'All' :filters.storeIds.length + ' selected' }}</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.stores" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllStores"
                                                    :checked="filters.storeIds.length === uniqueStoreIds.length">
                                                <span>{{ filters.storeIds.length === uniqueStoreIds.length ? 'DeselectAll' : 'Select All' }}</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label v-for="s in uniqueStoreIds" :key="s" class="checkbox-option">
                                                <input type="checkbox" :value="s" v-model="filters.storeIds"
                                                    @change="applyFilters">
                                                <span>Store {{ s }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Tank Nr filter -->
                            <div class="search-group">
                                <label class="search-label">Tank Nr</label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('tanks')">
                                        <span class="selected-items">{{ filters.tankNrs.length === 0 ? 'All' :filters.tankNrs.length + ' selected' }}</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.tanks" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllTanks"
                                                    :checked="filters.tankNrs.length === uniqueTankNrs.length">
                                                <span>{{ filters.tankNrs.length === uniqueTankNrs.length ? 'DeselectAll' : 'Select All' }}</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label v-for="t in uniqueTankNrs" :key="t" class="checkbox-option">
                                                <input type="checkbox" :value="t" v-model="filters.tankNrs"
                                                    @change="applyFilters">
                                                <span>Tank {{ t }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Type ID filter -->
                            <div class="search-group">
                                <label class="search-label">Type ID</label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('types')">
                                        <span class="selected-items">{{ filters.typeIds.length === 0 ? 'All' :
                                            filters.typeIds.length + ' selected' }}</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.types" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllTypes"
                                                    :checked="filters.typeIds.length === uniqueTypeIds.length">
                                                <span>{{ filters.typeIds.length === uniqueTypeIds.length ? 'DeselectAll' : 'Select All' }}</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label v-for="t in uniqueTypeIds" :key="t" class="checkbox-option">
                                                <input type="checkbox" :value="t" v-model="filters.typeIds"
                                                    @change="applyFilters">
                                                <span>Type {{ t }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Status filter -->
                            <div class="search-group">
                                <label class="search-label">Status</label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('statuses')">
                                        <span class="selected-items">{{ filters.statuses.length === 0 ? 'All' :
                                            filters.statuses.length + ' selected' }}</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.statuses" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllStatuses"
                                                    :checked="filters.statuses.length === uniqueStatuses.length">
                                                <span>{{ filters.statuses.length === uniqueStatuses.length ? 'DeselectAll' : 'Select All' }}</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label v-for="s in uniqueStatuses" :key="s" class="checkbox-option">
                                                <input type="checkbox" :value="s" v-model="filters.statuses"
                                                    @change="applyFilters">
                                                <span>{{ formatStatus(s) }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Product Name filter -->
                            <div class="search-group">
                                <label class="search-label">Product</label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('products')">
                                        <span class="selected-items">{{ filters.products.length === 0 ? 'All' :filters.products.length + ' selected' }}</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.products" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllProducts"
                                                    :checked="filters.products.length === uniqueProducts.length">
                                                <span>{{ filters.products.length === uniqueProducts.length ? 'DeselectAll' : 'Select All' }}</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label v-for="p in uniqueProducts" :key="p" class="checkbox-option">
                                                <input type="checkbox" :value="p" v-model="filters.products"
                                                    @change="applyFilters">
                                                <span>{{ p }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Date From -->
                            <div class="search-group">
                                <label class="search-label">Date From</label>
                                <input type="datetime-local" v-model="filters.dateFrom" @input="applyFilters"
                                    class="search-input">
                            </div>

                            <!-- Date To -->
                            <div class="search-group">
                                <label class="search-label">Date To</label>
                                <input type="datetime-local" v-model="filters.dateTo" @input="applyFilters"
                                    class="search-input">
                            </div>

                        </div>
                        <!-- Actions -->
                        <div class="filter-actions">
                            <button class="reset-filters-btn" @click="resetFilters">↺ Reset</button>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Table -->
            <div class="grid-container">
                <div class="grid-header">
                    <div class="grid-title">
                        <span class="report-name">Data Errors</span>
                        <span class="date-range-label">{{ filteredRecords.length }} records</span>
                    </div>
                    <div class="grid-controls">
                        <div v-if="loading" class="loading-indicator">
                            <div class="loader"></div><span>Loading...</span>
                        </div>
                        <div class="rows-selector">
                            <label class="rows-label">Show:</label>
                            <select v-model="itemsPerPage" @change="handleRowsChange" class="rows-select">
                                <option :value="25">25 rows</option>
                                <option :value="50">50 rows</option>
                                <option :value="100">100 rows</option>
                                <option :value="200">200 rows</option>
                            </select>
                        </div>
                        <div class="grid-actions">
                            <button class="action-button export-btn" @click="exportData"><span
                                    class="action-icon">📊</span><span>Export</span></button>
                        </div>
                    </div>
                </div>

                <div class="table-container">
                    <div v-if="loading" class="loading-overlay">
                        <div class="loader"></div>
                    </div>
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Create Date</th>
                                <th>Device ID</th>
                                <th>Store</th>
                                <th>Account</th>
                                <th>Tank Nr</th>
                                <th>Product</th>
                                <th>Type</th>
                                <th>Sub Type</th>
                                <th>Mm</th>
                                <th>Water Mm</th>
                                <th>Temp (°C)</th>
                                <th>Vol (L)</th>
                                <th>Vol TC (L)</th>
                                <th>Water Vol (L)</th>
                                <th>Weight (kg)</th>
                                <th>Prd Ratio</th>
                                <th>Water Ratio</th>
                                <th>Status</th>
                                <th>Message</th>
                                <th>State Info</th>
                            </tr>
                            <!-- Column search row -->
                            <tr class="search-row">
                                <th class="search-cell"><input v-model="colSearch.ID" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.CreateDate"
                                        @input="handleColumnSearch" class="column-search-input" placeholder="Search...">
                                </th>
                                <th class="search-cell"><input v-model="colSearch.DeviceID" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.Store" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.StoreID" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.TankNr" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.Prodname" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.TypeID" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.SubTypeID" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.Mm" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.WaterMm" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.Temp" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.Vol" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.VolTC" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.WaterVol" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.Weight" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.PrdRatio" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.WaterRatio"
                                        @input="handleColumnSearch" class="column-search-input" placeholder="Search...">
                                </th>
                                <th class="search-cell"><input v-model="colSearch.Status" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.Message" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                                <th class="search-cell"><input v-model="colSearch.StateInfo" @input="handleColumnSearch"
                                        class="column-search-input" placeholder="Search..."></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="!loading && paginatedRecords.length === 0">
                                <td colspan="21" class="empty-state">
                                    <div class="empty-icon">⚠️</div>
                                    <div class="empty-title">No Error Records Found</div>
                                    <div class="empty-description">No data errors for this account</div>
                                </td>
                            </tr>
                            <tr v-for="rec in paginatedRecords" :key="rec.ID" @click="selectedRecord = rec"
                                class="data-row" :class="getRowClass(rec)">
                                <td>{{ rec.ID }}</td>
                                <td>
                                    <div class="date-time-cell">
                                        <div class="date-part">{{ formatDateTime(rec.CreateDate) }}</div>
                                    </div>
                                </td>
                                <td><code class="id-code">{{ rec.DeviceID || '—' }}</code></td>
                                <td>{{ rec.Store || '—' }}</td>
                                <td>{{ rec.Account || '—' }}</td>
                                <td>{{ rec.TankNr != null ? rec.TankNr : '—' }}</td>
                                <td>{{ rec.Prodname || '—' }}</td>
                                <td>{{ rec.TypeID != null ? rec.TypeID == 1 ? "FMS" : rec.TypeID == 2 ? "ATG" : '—' :
                                    '—' }}</td>
                                <td>{{ rec.SubTypeID != null ? rec.SubTypeID : '—' }}</td>
                                <td>{{ formatFloat(rec.Mm) }}</td>
                                <td>{{ formatFloat(rec.WaterMm) }}</td>
                                <td>{{ formatFloat(rec.Temp) }}</td>
                                <td>{{ formatFloat(rec.Vol) }}</td>
                                <td>{{ formatFloat(rec.VolTC) }}</td>
                                <td>{{ formatFloat(rec.WaterVol) }}</td>
                                <td>{{ formatFloat(rec.Weight) }}</td>
                                <td>{{ formatFloat(rec.PrdRatio) }}</td>
                                <td>{{ formatFloat(rec.WaterRatio) }}</td>
                                <td>
                                    <span :class="['status-badge', 'st-' + (rec.Status || 0)]">
                                        {{ formatStatus(rec.Status) }}
                                    </span>
                                </td>
                                <td class="desc-cell">{{ rec.Message || '—' }}</td>
                                <td class="desc-cell">{{ rec.StateInfo || '—' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="totalPages > 1" class="pagination-container">
                    <div class="pagination-info">
                        Showing {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage,
                            filteredRecords.length) }}
                        of {{ filteredRecords.length }} entries
                    </div>
                    <div class="pagination-controls">
                        <button class="page-btn prev-btn" @click="currentPage = 1" :disabled="currentPage === 1">«
                            First</button>
                        <button class="page-btn prev-btn" @click="currentPage--" :disabled="currentPage === 1">‹
                            Prev</button>
                        <button v-for="p in visiblePageNumbers" :key="p" class="page-btn page-number"
                            :class="{ active: p === currentPage }" @click="currentPage = p">{{ p }}</button>
                        <button class="page-btn next-btn" @click="currentPage++"
                            :disabled="currentPage === totalPages">Next ›</button>
                        <button class="page-btn next-btn" @click="currentPage = totalPages"
                            :disabled="currentPage === totalPages">Last »</button>
                    </div>
                </div>

            </div><!-- /grid-container -->
        </div>
    </div>
</template>

<script>
export default {
    name: 'IOTDataErrorPage',

    data: function () {
        var pad = function (n) { return String(n).padStart(2, '0'); };
        var toLocalInput = function (d) {
            return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
                'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
        };
        var now = new Date();
        var threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);

        return {
            autoRefreshEnabled: false,
            refreshInterval: 60000,
            refreshTimer: null,
            _handleClickOutsideBound: null,
            loading: false,

            currentPage: 1,
            itemsPerPage: 25,
            records: [],
            statPopupType: null,

            filters: {
                deviceIds: [],
                storeIds: [],
                tankNrs: [],
                typeIds: [],
                statuses: [],
                products: [],
                dateFrom: toLocalInput(threeDaysAgo),
                dateTo: toLocalInput(now)
            },

            colSearch: {
                ID: '', CreateDate: '', DeviceID: '', StoreID: '', Store: '', TankNr: '',
                Prodname: '', TypeID: '', SubTypeID: '', Mm: '', WaterMm: '',
                Temp: '', Vol: '', VolTC: '', WaterVol: '', Weight: '',
                PrdRatio: '', WaterRatio: '', Status: '', Message: '', StateInfo: ''
            },

            filtersVisible: true,
            statsVisible: true,

            dropdownStates: {
                devices: false,
                stores: false,
                tanks: false,
                types: false,
                statuses: false,
                products: false
            },

            uniqueDevices: [],
            uniqueStoreIds: [],
            uniqueTankNrs: [],
            uniqueTypeIds: [],
            uniqueStatuses: [],
            uniqueProducts: [],

            selectedRecord: null
        };
    },

    computed: {
        summaryStats: function () {
            var devices = new Set();
            this.filteredRecords.forEach(function (r) {
                if (r.DeviceID) devices.add(r.DeviceID);
            });
            return {
                totalErrors: this.filteredRecords.length,
                uniqueDevices: devices.size
            };
        },

        activeFilterCount: function () {
            var f = this.filters, count = 0;
            if (f.deviceIds.length) count++;
            if (f.storeIds.length) count++;
            if (f.tankNrs.length) count++;
            if (f.typeIds.length) count++;
            if (f.statuses.length) count++;
            if (f.products.length) count++;
            if (f.dateFrom) count++;
            if (f.dateTo) count++;
            return count;
        },

        filteredRecords: function () {
            var self = this;
            var filtered = this.records.slice();

            if (this.filters.deviceIds.length > 0)
                filtered = filtered.filter(function (r) { return self.filters.deviceIds.indexOf(r.DeviceID) !== -1; });
            if (this.filters.storeIds.length > 0)
                filtered = filtered.filter(function (r) { return self.filters.storeIds.indexOf(r.StoreID) !== -1; });
            if (this.filters.tankNrs.length > 0)
                filtered = filtered.filter(function (r) { return self.filters.tankNrs.indexOf(r.TankNr) !== -1; });
            if (this.filters.typeIds.length > 0)
                filtered = filtered.filter(function (r) { return self.filters.typeIds.indexOf(r.TypeID) !== -1; });
            if (this.filters.statuses.length > 0)
                filtered = filtered.filter(function (r) { return self.filters.statuses.indexOf(r.Status) !== -1; });
            if (this.filters.products.length > 0)
                filtered = filtered.filter(function (r) { return self.filters.products.indexOf(r.Prodname) !== -1; });

            if (this.filters.dateFrom) {
                var fromTs = new Date(this.filters.dateFrom).getTime();
                filtered = filtered.filter(function (r) { return r.CreateDate && new Date(r.CreateDate).getTime() >= fromTs; });
            }
            if (this.filters.dateTo) {
                var toTs = new Date(this.filters.dateTo).getTime();
                filtered = filtered.filter(function (r) { return r.CreateDate && new Date(r.CreateDate).getTime() <= toTs; });
            }

            var cs = this.colSearch;
            filtered = filtered.filter(function (r) {
                if (cs.ID && !String(r.ID || '').includes(cs.ID)) return false;
                if (cs.CreateDate && !self.formatDateTime(r.CreateDate).toLowerCase().includes(cs.CreateDate.toLowerCase())) return false;
                if (cs.DeviceID && !(r.DeviceID || '').toLowerCase().includes(cs.DeviceID.toLowerCase())) return false;
                if (cs.StoreID && !String(r.StoreID != null ? r.StoreID : '').includes(cs.StoreID)) return false;
                if (cs.Store && !(r.Store || '').toLowerCase().includes(cs.Store.toLowerCase())) return false;
                if (cs.TankNr && !String(r.TankNr != null ? r.TankNr : '').includes(cs.TankNr)) return false;
                if (cs.Prodname && !(r.Prodname || '').toLowerCase().includes(cs.Prodname.toLowerCase())) return false;
                if (cs.TypeID && !String(r.TypeID != null ? r.TypeID : '').includes(cs.TypeID)) return false;
                if (cs.SubTypeID && !String(r.SubTypeID != null ? r.SubTypeID : '').includes(cs.SubTypeID)) return false;
                if (cs.Mm && !self.formatFloat(r.Mm).includes(cs.Mm)) return false;
                if (cs.WaterMm && !self.formatFloat(r.WaterMm).includes(cs.WaterMm)) return false;
                if (cs.Temp && !self.formatFloat(r.Temp).includes(cs.Temp)) return false;
                if (cs.Vol && !self.formatFloat(r.Vol).includes(cs.Vol)) return false;
                if (cs.VolTC && !self.formatFloat(r.VolTC).includes(cs.VolTC)) return false;
                if (cs.WaterVol && !self.formatFloat(r.WaterVol).includes(cs.WaterVol)) return false;
                if (cs.Weight && !self.formatFloat(r.Weight).includes(cs.Weight)) return false;
                if (cs.PrdRatio && !self.formatFloat(r.PrdRatio).includes(cs.PrdRatio)) return false;
                if (cs.WaterRatio && !self.formatFloat(r.WaterRatio).includes(cs.WaterRatio)) return false;
                if (cs.Status && !self.formatStatus(r.Status).toLowerCase().includes(cs.Status.toLowerCase())) return false;
                if (cs.Message && !(r.Message || '').toLowerCase().includes(cs.Message.toLowerCase())) return false;
                if (cs.StateInfo && !(r.StateInfo || '').toLowerCase().includes(cs.StateInfo.toLowerCase())) return false;
                return true;
            });

            return filtered;
        },

        totalPages: function () { return Math.ceil(this.filteredRecords.length / this.itemsPerPage); },

        paginatedRecords: function () {
            var start = (this.currentPage - 1) * this.itemsPerPage;
            return this.filteredRecords.slice(start, start + this.itemsPerPage);
        },

        visiblePageNumbers: function () {
            var maxV = 5, pages = [];
            var start = Math.max(1, this.currentPage - 2);
            var end = Math.min(this.totalPages, start + maxV - 1);
            if (end - start < maxV - 1) start = Math.max(1, end - maxV + 1);
            for (var i = start; i <= end; i++) pages.push(i);
            return pages;
        }
    },

    methods: {

        formatFloat: function (val) {
            if (val == null) return '—';
            return parseFloat(val).toFixed(2);
        },

        formatStatus: function (status) {
            if (status == null) return '—';
            var map = { 0: 'OK', 1: 'Warning', 2: 'Error', 3: 'Critical' };
            return map[status] || 'Status ' + status;
        },

        formatDateTime: function (date) {
            if (!date) return 'N/A';
            var d = new Date(date);
            return d.getFullYear() + '/' +
                String(d.getMonth() + 1).padStart(2, '0') + '/' +
                String(d.getDate()).padStart(2, '0') + ' ' +
                String(d.getHours()).padStart(2, '0') + ':' +
                String(d.getMinutes()).padStart(2, '0');
        },

        getRowClass: function (rec) {
            if (rec.Status === 3) return 'row-critical';
            if (rec.Status === 2) return 'row-failed';
            if (rec.Status === 1) return 'row-warning';
            return 'row-success';
        },

        openStatPopup: function (type) { this.statPopupType = type; },

        getStatPopupTitle: function () {
            if (this.statPopupType === 'total') return '📝 All Filtered Records';
            return 'Records';
        },

        getStatPopupRecords: function () {
            if (this.statPopupType === 'total') return this.filteredRecords;
            return [];
        },

        loadData: function () {
            var self = this;
            self.loading = true;

            var url = 'VwIotdataNotificationComprehensiveActivity/GetIOTErrors?accountId=' + localStorage.getItem('userAccountId').toString();

            var safetyTimer = setTimeout(function () {
                if (self.loading) {
                    self.loading = false;
                    self.showNotification('Warning', 'Request timed out — check API connectivity', 'warning');
                }
            }, 15000);

            var onSuccess = function (response) {
                var data = (response && response.data !== undefined) ? response.data : response;

                if (data && !Array.isArray(data) && (data.error === true || data.Error === 1)) {
                    self.showNotification('Error', data.message || data.Message || 'API error', 'danger');
                    self.records = [];
                    return;
                }

                var list = Array.isArray(data) ? data : (data && Array.isArray(data.data) ? data.data : null);

                if (list) {
                    self.records = list.map(function (item) {
                        return {
                            ID: item.id != null ? item.id : item.id,
                            AccountID: item.accountID != null ? item.accountID : item.accountID,
                            Account: item.account || item.account || '',
                            DeviceID: item.seviceID || item.deviceID || '',
                            StoreID: item.storeID != null ? parseInt(item.storeID) : null,
                            Store: item.store || item.store || '',
                            CreateDate: item.createDate || item.createDate,
                            Message: item.message || item.message || '',
                            TypeID: item.typeID != null ? item.typeID : null,
                            SubTypeID: item.subTypeID != null ? item.subTypeID : null,
                            TankNr: item.tankNr != null ? item.tankNr : null,
                            Mm: item.mm != null ? parseFloat(item.mm) : null,
                            WaterMm: item.waterMm != null ? parseFloat(item.waterMm) : null,
                            Temp: item.temp != null ? parseFloat(item.temp) : null,
                            Vol: item.vol != null ? parseFloat(item.vol) : null,
                            VolTC: item.volTC != null ? parseFloat(item.volTC) : null,
                            Weight: item.weight != null ? parseFloat(item.weight) : null,
                            WaterVol: item.waterVol != null ? parseFloat(item.waterVol) : null,
                            PrdRatio: item.prdRatio != null ? parseFloat(item.prdRatio) : null,
                            WaterRatio: item.waterRatio != null ? parseFloat(item.waterRatio) : null,
                            Status: item.status != null ? parseInt(item.status) : null,
                            Prodname: item.prodname || item.prodname || '',
                            StateInfo: item.stateInfo || item.stateInfo || ''
                        };
                    });
                    self.extractUniqueValues();
                    self.showNotification('Success', 'Loaded ' + self.records.length + ' error records', 'success');
                } else {
                    self.records = [];
                    self.showNotification('Warning', 'No data returned', 'warning');
                }
            };

            var onFinally = function () {
                clearTimeout(safetyTimer);
                self.loading = false;
            };

            try {
                self.$ajaxGet(self, url, onSuccess, onFinally);
            } catch (e) {
                clearTimeout(safetyTimer);
                self.loading = false;
                self.showNotification('Error', 'Failed to reach API: ' + e.message, 'danger');
                console.error('loadData error:', e);
            }
        },

        extractUniqueValues: function () {
            var devSet = {}, storeSet = {}, tankSet = {}, typeSet = {}, statusSet = {}, prodSet = {};
            this.records.forEach(function (r) {
                if (r.DeviceID) devSet[r.DeviceID] = true;
                if (r.StoreID != null) storeSet[r.StoreID] = true;
                if (r.TankNr != null) tankSet[r.TankNr] = true;
                if (r.TypeID != null) typeSet[r.TypeID] = true;
                if (r.Status != null) statusSet[r.Status] = true;
                if (r.Prodname) prodSet[r.Prodname] = true;
            });
            this.uniqueDevices = Object.keys(devSet).sort().map(function (k) { return { id: k }; });
            this.uniqueStoreIds = Object.keys(storeSet).map(Number).sort(function (a, b) { return a - b; });
            this.uniqueTankNrs = Object.keys(tankSet).map(Number).sort(function (a, b) { return a - b; });
            this.uniqueTypeIds = Object.keys(typeSet).map(Number).sort(function (a, b) { return a - b; });
            this.uniqueStatuses = Object.keys(statusSet).map(Number).sort(function (a, b) { return a - b; });
            this.uniqueProducts = Object.keys(prodSet).sort();
        },

        applyFilters: function () { this.currentPage = 1; },
        handleColumnSearch: function () { this.currentPage = 1; },
        handleRowsChange: function () { this.currentPage = 1; },
        refreshData: function () { this.showNotification('Info', 'Refreshing...', 'primary'); this.loadData(); },

        resetFilters: function () {
            var pad = function (n) { return String(n).padStart(2, '0'); };
            var toLocalInput = function (d) {
                return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
                    'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
            };
            var now = new Date();
            var threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);

            this.filters = {
                deviceIds: [], storeIds: [], tankNrs: [], typeIds: [], statuses: [], products: [],
                dateFrom: toLocalInput(threeDaysAgo),
                dateTo: toLocalInput(now)
            };
            this.colSearch = {
                ID: '', CreateDate: '', DeviceID: '', StoreID: '', Store: '', TankNr: '', Prodname: '', TypeID: '',
                SubTypeID: '', Mm: '', WaterMm: '', Temp: '', Vol: '', VolTC: '', WaterVol: '',
                Weight: '', PrdRatio: '', WaterRatio: '', Status: '', Message: '', StateInfo: ''
            };
            this.currentPage = 1;
            this.loadData();
            this.showNotification('Success', 'Filters reset to last 3 days', 'success');
        },

        toggleDropdown: function (type) {
            var self = this;
            Object.keys(this.dropdownStates).forEach(function (k) { if (k !== type) self.dropdownStates[k] = false; });
            this.dropdownStates[type] = !this.dropdownStates[type];
        },

        toggleAllDevices: function () { this.filters.deviceIds = this.filters.deviceIds.length === this.uniqueDevices.length ? [] : this.uniqueDevices.map(function (d) { return d.id; }); this.applyFilters(); },
        toggleAllStores: function () { this.filters.storeIds = this.filters.storeIds.length === this.uniqueStoreIds.length ? [] : this.uniqueStoreIds.slice(); this.applyFilters(); },
        toggleAllTanks: function () { this.filters.tankNrs = this.filters.tankNrs.length === this.uniqueTankNrs.length ? [] : this.uniqueTankNrs.slice(); this.applyFilters(); },
        toggleAllTypes: function () { this.filters.typeIds = this.filters.typeIds.length === this.uniqueTypeIds.length ? [] : this.uniqueTypeIds.slice(); this.applyFilters(); },
        toggleAllStatuses: function () { this.filters.statuses = this.filters.statuses.length === this.uniqueStatuses.length ? [] : this.uniqueStatuses.slice(); this.applyFilters(); },
        toggleAllProducts: function () { this.filters.products = this.filters.products.length === this.uniqueProducts.length ? [] : this.uniqueProducts.slice(); this.applyFilters(); },

        exportData: function () {
            var self = this;
            var headers = 'ID,Create Date,Device ID,Store ID,Store,Tank Nr,Product,Type ID,Sub Type,Mm,Water Mm,Temp,Vol,Vol TC,Water Vol,Weight,Prd Ratio,Water Ratio,Status,Message,State Info';
            var rows = this.filteredRecords.map(function (r) {
                return [
                    r.ID, self.formatDateTime(r.CreateDate), r.DeviceID || '',
                    r.StoreID != null ? r.StoreID : '', r.Store || '',
                    r.TankNr != null ? r.TankNr : '', r.Prodname || '',
                    r.TypeID != null ? r.TypeID : '', r.SubTypeID != null ? r.SubTypeID : '',
                    self.formatFloat(r.Mm), self.formatFloat(r.WaterMm), self.formatFloat(r.Temp),
                    self.formatFloat(r.Vol), self.formatFloat(r.VolTC), self.formatFloat(r.WaterVol),
                    self.formatFloat(r.Weight), self.formatFloat(r.PrdRatio), self.formatFloat(r.WaterRatio),
                    self.formatStatus(r.Status), r.Message || '', r.StateInfo || ''
                ].map(function (v) { return '"' + String(v).replace(/"/g, '""') + '"'; }).join(',');
            }).join('\n');
            var a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([[headers, rows].join('\n')], { type: 'text/csv' }));
            a.download = 'iot_data_errors_' + Date.now() + '.csv';
            a.click();
            this.showNotification('Success', 'Data exported', 'success');
        },

        handleAutoRefreshToggle: function () {
            if (this.autoRefreshEnabled) { this.startAutoRefresh(); this.showNotification('Success', 'Auto-refresh enabled', 'success'); }
            else { this.stopAutoRefresh(); this.showNotification('Info', 'Auto-refresh disabled', 'primary'); }
        },
        handleRefreshIntervalChange: function () { if (this.autoRefreshEnabled) { this.stopAutoRefresh(); this.startAutoRefresh(); } },
        startAutoRefresh: function () {
            var self = this; this.stopAutoRefresh();
            if (this.autoRefreshEnabled) this.refreshTimer = setInterval(function () { self.loadData(); }, this.refreshInterval);
        },
        stopAutoRefresh: function () { if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null; } },
        formatRefreshInterval: function () { var s = this.refreshInterval / 1000; return s < 60 ? s + 's' : (s / 60) + 'm'; },

        showNotification: function (title, text, type) {
            if (this.$vs && this.$vs.notify) this.$vs.notify({ title: title, text: text, color: type, position: 'top-right' });
            else console.log('[' + title + '] ' + text);
        },

        handleClickOutside: function (event) {
            if (!this.$el) return;
            var inside = false, self = this;
            this.$el.querySelectorAll('.multi-select-container').forEach(function (c) { if (c.contains(event.target)) inside = true; });
            if (!inside) Object.keys(this.dropdownStates).forEach(function (k) { self.dropdownStates[k] = false; });
        }
    },

    mounted: function () {
        var self = this;
        this.loadData();
        this._handleClickOutsideBound = function (event) { self.handleClickOutside(event); };
        document.addEventListener('click', this._handleClickOutsideBound);
        this.showNotification('Welcome!', 'Error Monitor — Ready', 'success');
    },

    beforeDestroy: function () {
        this.stopAutoRefresh();
        if (this._handleClickOutsideBound) document.removeEventListener('click', this._handleClickOutsideBound);
    }
};
</script>

<style>
:root {
    --autumn-primary: #D2691E;
    --autumn-accent: #FF8C00;
}

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #e2e8f0;
    height: 100vh;
    overflow: hidden;
    width: 100%;
}

#app {
    height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    max-width: none;
}

.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 16px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 20px;
}

.logo-section {
    display: flex;
    align-items: center;
    gap: 12px;
}

.logo-icon {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: var(--autumn-primary);
}

.dashboard-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 600;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.loader-controls {
    display: flex;
    align-items: center;
    gap: 15px;
}

.toggle-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
}

.toggle-checkbox {
    width: 18px;
    height: 18px;
    margin-right: 8px;
    cursor: pointer;
    accent-color: white;
}

.toggle-text {
    color: white;
    font-size: .875rem;
    font-weight: 500;
}

.interval-label {
    display: flex;
    align-items: center;
    gap: 8px;
}

.interval-text {
    color: white;
    font-size: .875rem;
    font-weight: 500;
}

.interval-select {
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, .3);
    background: rgba(255, 255, 255, .2);
    color: white;
    font-size: .875rem;
    cursor: pointer;
}

.interval-select option {
    background: #FF8C00;
    color: white;
}

.live-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, .2);
    padding: 8px 16px;
    border-radius: 20px;
}

.live-dot {
    width: 8px;
    height: 8px;
    background: #4CAF50;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

.live-dot.auto-refresh {
    animation: pulse 1s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(76, 175, 80, .7)
    }

    70% {
        box-shadow: 0 0 0 10px rgba(76, 175, 80, 0)
    }

    100% {
        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0)
    }
}

.header-refresh-btn {
    padding: 8px 16px;
    background: rgba(255, 255, 255, .2);
    border: 1px solid rgba(255, 255, 255, .3);
    border-radius: 6px;
    color: white;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: 12px;
}

.header-refresh-btn:hover {
    background: rgba(255, 255, 255, .3);
    transform: translateY(-1px);
}

.refresh-icon {
    display: inline-block;
    transition: transform .3s;
}

.header-refresh-btn:hover .refresh-icon {
    transform: rotate(180deg);
}

.main-content {
    flex: 1;
    overflow: auto;
    padding: 20px;
    width: 100%;
    background: #e2e8f0;
}

.stats-section {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    border: 1px solid #cbd5e1;
}

.stats-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.stats-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
    margin: 0;
}

.toggle-stats-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.toggle-stats-btn:hover {
    background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
    transform: translateY(-1px);
}

.stats-wrapper {
    padding: 16px 20px;
}

.stats-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.stat-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    transition: all .3s;
    border: 1px solid #e2e8f0;
    position: relative;
    overflow: visible;
}

.clickable-stat {
    cursor: pointer;
}

.clickable-stat:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, .15);
    background: #fff;
}

.stat-hint {
    position: absolute;
    bottom: 8px;
    right: 8px;
    font-size: .625rem;
    color: #94a3b8;
    opacity: 0;
    transition: opacity .3s;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: .5px;
}

.clickable-stat:hover .stat-hint {
    opacity: 1;
}

.total-stat {
    border-left: 4px solid #FF8C00;
}

.volume-stat {
    border-left: 4px solid #4CAF50;
}

.cost-stat {
    border-left: 4px solid #27AE60;
}

.equipment-stat {
    border-left: 4px solid #E74C3C;
}

.product-stat {
    border-left: 4px solid #2196F3;
}

.average-stat {
    border-left: 4px solid #9C27B0;
}

.stat-label {
    color: #2c3e50;
    font-size: .875rem;
    margin-bottom: 8px;
    font-weight: 600;
}

.stat-value {
    font-size: 1.75rem;
    color: #2c3e50;
    font-weight: bold;
}

.stat-change {
    font-size: .75rem;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.stat-change.positive {
    color: #27AE60;
}

.stat-change.negative {
    color: #E74C3C;
}

.search-section {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    border: 1px solid #cbd5e1;
}

.filter-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.filter-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
    margin: 0;
}

.filter-header-buttons {
    display: flex;
    gap: 10px;
    align-items: center;
}

.toggle-filter-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.toggle-filter-btn:hover {
    background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
    transform: translateY(-1px);
}

.reset-filter-header-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border: none;
    color: white;
    font-size: .9rem;
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all .3s;
    display: flex;
    align-items: center;
    gap: 6px;
}

.reset-filter-header-btn:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, .3);
}

.reset-icon {
    font-size: 1.1rem;
    font-weight: bold;
    display: inline-block;
    transition: transform .3s;
}

.reset-filter-header-btn:hover .reset-icon {
    transform: rotate(180deg);
}

.search-section.collapsed .filter-header {
    border-radius: 12px;
    border-bottom: none;
}

.search-controls-wrapper {
    padding: 16px 20px;
}

.search-controls {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}

.search-group {
    display: flex;
    flex-direction: column;
    position: relative;
}

.search-label {
    font-size: .875rem;
    color: #34495E;
    margin-bottom: 4px;
    font-weight: 500;
}

.search-input {
    padding: 10px 12px;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    font-size: 14px;
    transition: all .3s;
    background: #fff;
}

.search-input:focus {
    outline: none;
    border-color: #D2691E;
    box-shadow: 0 0 0 3px rgba(210, 105, 30, .15);
}

.multi-select-container {
    position: relative;
}

.multi-select-display {
    padding: 10px 12px;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all .3s;
    min-height: 44px;
}

.multi-select-display:hover {
    border-color: #D2691E;
    background: #fff8f3;
}

.placeholder {
    color: #999;
}

.selected-items {
    color: #495057;
    font-weight: 500;
}

.dropdown-arrow {
    color: #999;
    font-size: .75rem;
}

.multi-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 2px solid #D2691E;
    border-radius: 8px;
    margin-top: 4px;
    max-height: 250px;
    overflow-y: auto;
    z-index: 9999;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
}

.select-all-option {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    background: #f8f9fa;
}

.dropdown-options {
    max-height: 200px;
    overflow-y: auto;
}

.checkbox-option {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    transition: background .2s;
}

.checkbox-option:hover {
    background: #fff3e0;
}

.checkbox-option input[type="checkbox"] {
    margin-right: 8px;
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #D2691E;
}

.multi-badge {
    background: #FF8C00;
    color: white;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: .75rem;
    font-weight: 600;
    margin-left: 4px;
}

.orange-badge {
    background: #FF8C00;
}

.columns-select {
    border: 2px solid #FF8C00;
    background: #FFF8F3;
}

.columns-dropdown {
    max-height: 400px;
}

.filter-actions {
    display: flex;
    gap: 12px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
}

.apply-filters-btn {
    padding: 10px 24px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all .3s;
}

.apply-filters-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(210, 105, 30, .3);
}

.reset-filters-btn {
    padding: 10px 24px;
    background: white;
    color: #34495E;
    border: 2px solid #FFE4B5;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all .3s;
}

.reset-filters-btn:hover {
    background: #FFF8F3;
    border-color: #FF8C00;
    transform: translateY(-1px);
}

.grid-container {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    overflow: hidden;
    border: 1px solid #cbd5e1;
}

.grid-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.grid-title {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.report-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #334155;
}

.date-range-label {
    font-size: .9rem;
    color: #64748b;
    font-weight: 500;
    font-style: italic;
}

.grid-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.rows-selector {
    display: flex;
    align-items: center;
    gap: 8px;
}

.rows-label {
    font-size: .875rem;
    color: #6c757d;
    font-weight: 500;
}

.rows-select {
    padding: 6px 12px;
    border: 1px solid #94a3b8;
    border-radius: 6px;
    background: white;
    color: #475569;
    font-size: .875rem;
    cursor: pointer;
    min-width: 100px;
}

.grid-actions {
    display: flex;
    gap: 8px;
}

.action-button {
    padding: 8px 16px;
    background: white;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    color: #475569;
    font-size: .875rem;
    cursor: pointer;
    transition: all .3s;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    white-space: nowrap;
}

.action-button:hover {
    background: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
}

.export-btn {
    color: #10B981;
    border-color: #D1FAE5;
}

.export-btn:hover {
    background: #ECFDF5;
    border-color: #10B981;
}

.popout-btn {
    color: #8B5CF6;
    border-color: #DDD6FE;
}

.popout-btn:hover {
    background: #F3E8FF;
    border-color: #8B5CF6;
}

.refresh-btn {
    color: #3B82F6;
    border-color: #DBEAFE;
}

.refresh-btn:hover {
    background: #EFF6FF;
    border-color: #3B82F6;
}

.clear-column-searches-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .2s;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-right: 10px;
}

.clear-column-searches-btn:hover {
    transform: translateY(-1px);
}

.table-container {
    overflow: auto;
    position: relative;
    max-height: 600px;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table thead {
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    z-index: 10;
}

.data-table th {
    padding: 12px 16px;
    text-align: left;
    color: white;
    font-weight: 600;
    font-size: .875rem;
    text-transform: uppercase;
    letter-spacing: .5px;
    white-space: nowrap;
}

.data-table tbody tr {
    border-bottom: 1px solid #e2e8f0;
    transition: all .2s;
    background: #fff;
    cursor: pointer;
}

.data-table tbody tr:hover {
    background: #f1f5f9;
}

.data-table tbody tr.row-success {
    background: rgba(39, 174, 96, .03);
}

.data-table tbody tr.row-failed {
    background: rgba(231, 76, 60, .04);
    border-left: 3px solid #E74C3C;
}

.data-table td {
    padding: 12px 16px;
    font-size: .875rem;
    color: #334155;
}

.search-row {
    background: #fff3e0 !important;
}

.search-row th {
    background: transparent !important;
    padding: 6px !important;
    border-right: 1px solid #ffcc80 !important;
}

.column-search-input {
    width: 100%;
    padding: 5px 8px;
    border: 1px solid #ffcc80;
    border-radius: 4px;
    font-size: .75rem;
    background: white;
    color: #333;
    transition: all .2s;
    font-weight: normal;
    letter-spacing: normal;
    text-transform: none;
}

.column-search-input:focus {
    outline: none;
    border-color: #ff9800;
    box-shadow: 0 0 0 2px rgba(255, 152, 0, .2);
}

.column-search-input::placeholder {
    color: #999;
    font-style: italic;
}

.date-time-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.date-part {
    font-size: .875rem;
    color: #334155;
    font-weight: 500;
}

.time-ago {
    font-size: .75rem;
    color: #94a3b8;
    font-style: italic;
}

.date-cell {
    font-family: monospace;
    color: #475569;
}

.transaction-id {
    font-family: 'Courier New', monospace;
    font-size: .75rem;
}

.transaction-id.colorful {
    color: #ec4899;
    background: rgba(236, 72, 153, .1);
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
}

.id-code {
    font-family: monospace;
    font-size: .75rem;
    color: #64748b;
}

.registration-badge {
    display: inline-block;
    background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
    color: white;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: .8rem;
    font-weight: 600;
    font-family: monospace;
    letter-spacing: .5px;
}

.tag-code {
    background: #e2e8f0;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: .75rem;
    color: #334155;
    font-family: monospace;
}

.product-badge-new {
    display: inline-block;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: .8rem;
    font-weight: 600;
}

.product-diesel {
    background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
    color: white;
}

.product-petrol {
    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
    color: white;
}

.product-gas {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    color: white;
}

.product-other {
    background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
    color: white;
}

.volume-cell-new {
    display: inline-flex;
    align-items: baseline;
    gap: 3px;
    font-weight: 700;
}

.volume-value {
    font-size: 1rem;
    color: #f97316;
}

.volume-unit {
    font-size: .875rem;
    color: #fb923c;
    font-weight: 500;
}

.volume-primary {
    font-weight: 600;
    color: #FF8C00;
    font-size: .9rem;
}

.equipment-name {
    color: #2196F3;
    font-weight: 500;
}

.metric-value {
    font-weight: 500;
    color: #475569;
}

.personnel-name {
    color: #9C27B0;
    font-weight: 500;
}

.error-text {
    color: #E74C3C;
    font-size: .8rem;
    font-style: italic;
    cursor: help;
}

.count-value {
    font-weight: 700;
    color: #FF8C00;
    font-size: 1rem;
}

.status-pill {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .3px;
}

.status-200 {
    background: rgba(39, 174, 96, .15);
    color: #27AE60;
    border: 1px solid rgba(39, 174, 96, .3);
}

.status-401 {
    background: rgba(231, 76, 60, .15);
    color: #E74C3C;
    border: 1px solid rgba(231, 76, 60, .3);
}

.status-400 {
    background: rgba(255, 152, 0, .15);
    color: #FF9800;
    border: 1px solid rgba(255, 152, 0, .3);
}

.status-422 {
    background: rgba(155, 89, 182, .15);
    color: #9B59B6;
    border: 1px solid rgba(155, 89, 182, .3);
}

.status-default {
    background: rgba(100, 116, 139, .1);
    color: #64748b;
    border: 1px solid rgba(100, 116, 139, .3);
}

.status-cell-new {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.status-dot-new {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

.status-dot-new.processed {
    background: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, .2);
    animation: pulse-green 2s infinite;
}

.status-dot-new.failed {
    background: #ef4444;
}

.status-text-new {
    font-size: .8rem;
    color: #10b981;
    font-weight: 600;
}

.text-failed {
    color: #ef4444;
    font-weight: 600;
    font-size: .8rem;
}

@keyframes pulse-green {
    0% {
        box-shadow: 0 0 0 2px rgba(16, 185, 129, .2)
    }

    70% {
        box-shadow: 0 0 0 6px rgba(16, 185, 129, 0)
    }

    100% {
        box-shadow: 0 0 0 2px rgba(16, 185, 129, .2)
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #334155;
    background: #f8fafc;
    border-radius: 8px;
    margin: 20px;
}

.empty-icon {
    font-size: 4rem;
    color: #FFE4B5;
    margin-bottom: 16px;
}

.empty-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 8px;
}

.empty-description {
    color: #6c757d;
    text-align: center;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(241, 245, 249, .95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
}

.loader {
    width: 48px;
    height: 48px;
    border: 4px solid #FFE4B5;
    border-top: 4px solid #D2691E;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg)
    }

    100% {
        transform: rotate(360deg)
    }
}

.pagination-container {
    padding: 16px 20px;
    border-top: 1px solid #cbd5e1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f1f5f9;
}

.pagination-info {
    color: #6c757d;
    font-size: .875rem;
}

.pagination-controls {
    display: flex;
    gap: 4px;
}

.page-btn {
    padding: 6px 12px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    color: #495057;
    font-size: .875rem;
    cursor: pointer;
    transition: all .2s;
    font-weight: 500;
    min-width: 40px;
}

.page-btn:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #D2691E;
    color: #D2691E;
}

.page-btn.active {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-color: #D2691E;
}

.page-btn:disabled {
    opacity: .5;
    cursor: not-allowed;
}

.simple-popup {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, .5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}

.drill-down-overlay {
    z-index: 99999 !important;
    background: rgba(0, 0, 0, .6);
}

.popup-content {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 700px;
    max-height: 80vh;
    overflow: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, .3);
}

.wide-popup {
    max-width: 900px;
}

.drill-down-popup {
    position: fixed !important;
    max-width: 1200px;
    width: 90%;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(0, 0, 0, .4);
    border: 2px solid #FF8C00;
    z-index: 100000;
}

.popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e0e0e0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-radius: 8px 8px 0 0;
}

.popup-header h3 {
    margin: 0;
    font-size: 1.25rem;
}

.close-btn {
    background: rgba(255, 255, 255, .2);
    border: none;
    color: white;
    font-size: 28px;
    cursor: pointer;
    padding: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all .2s;
    line-height: 1;
}

.close-btn:hover {
    background: rgba(255, 255, 255, .3);
    transform: scale(1.1);
}

.drill-down-header {
    cursor: move;
    user-select: none;
}

.header-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
}

.popup-date-range {
    font-size: .85rem;
    color: rgba(255, 255, 255, .9);
}

.popup-body {
    padding: 20px;
}

.drill-down-popup .popup-body {
    padding: 20px 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.detail-sections {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.detail-section {
    background: #f8fafc;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e2e8f0;
}

.detail-section h4 {
    margin: 0 0 12px 0;
    color: #334155;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
}

.source-icon {
    font-size: 1.25rem;
}

.equipment-section {
    border-left: 3px solid #3498DB;
}

.cost-section {
    border-left: 3px solid #FF9800;
}

.personnel-section {
    border-left: 3px solid #9C27B0;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
}

.detail-row {
    font-size: .9rem;
}

.detail-row strong {
    color: #34495E;
    display: inline-block;
    min-width: 100px;
}

.drill-down-summary {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.summary-card {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.summary-label {
    font-size: .875rem;
    color: #64748b;
    font-weight: 500;
}

.summary-value-large {
    font-size: 1.5rem;
    font-weight: 700;
    color: #FF8C00;
}

.summary-stats {
    display: flex;
    gap: 30px;
    align-items: center;
}

.stat-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.drill-down-actions {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    padding: 0 16px;
}

.action-btn {
    padding: 8px 16px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: white;
    color: #475569;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .2s;
    display: flex;
    align-items: center;
    gap: 6px;
}

.action-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
}

.excel-btn:hover {
    background: #10b981;
    color: white;
    border-color: #10b981;
}

.print-btn:hover {
    background: #8b5cf6;
    color: white;
    border-color: #8b5cf6;
}

.drill-down-grid {
    flex: 1;
    overflow: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin: 0 16px;
}

.drill-down-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
}

.drill-down-table thead {
    background: #f1f5f9;
    position: sticky;
    top: 0;
    z-index: 10;
}

.drill-down-table th {
    padding: 10px;
    text-align: left;
    font-weight: 600;
    font-size: .875rem;
    color: #475569;
    border-bottom: 2px solid #e2e8f0;
}

.drill-down-table tbody tr {
    border-bottom: 1px solid #f0f0f0;
    transition: background .2s;
}

.drill-down-row {
    cursor: pointer;
}

.drill-down-row:hover {
    background: #f8fafc;
}

.drill-down-table td {
    padding: 10px;
    font-size: .875rem;
    color: #334155;
}

.product-badge-small {
    padding: 3px 8px;
    font-size: .75rem;
    border-radius: 12px;
    display: inline-block;
}

.registration-code {
    font-family: monospace;
    font-size: .8rem;
    background: #f0f9ff;
    padding: 2px 6px;
    border-radius: 4px;
    color: #0369a1;
}

.volume-cell-drill {
    font-weight: 600;
    color: #f97316;
    font-size: .9rem;
}

.drill-down-totals {
    padding: 12px 16px;
    background: #f8fafc;
    border-top: 2px solid #e2e8f0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
}

.total-label {
    font-weight: 600;
    color: #475569;
    font-size: .95rem;
}

.total-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #FF8C00;
}

.drill-down-footer {
    padding: 12px 16px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    text-align: right;
    border-radius: 0 0 8px 8px;
}

.record-count {
    font-size: .875rem;
    color: #64748b;
}

.fields-section {
    grid-column: 1 / -1;
}

.fields-table {
    width: 100%;
    border-collapse: collapse;
    font-size: .875rem;
    margin-top: 8px;
}

.fields-table thead {
    background: #f1f5f9;
}

.fields-table th {
    padding: 8px 12px;
    text-align: left;
    font-weight: 600;
    color: #475569;
    border-bottom: 2px solid #e2e8f0;
}

.fields-table tbody tr {
    border-bottom: 1px solid #f0f4f8;
}

.fields-table tbody tr:hover {
    background: #f8fafc;
}

.field-key {
    padding: 7px 12px;
    font-weight: 600;
    color: #334155;
    width: 40%;
}

.field-value {
    padding: 7px 12px;
    color: #475569;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all .3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

@media (max-width: 768px) {
    .main-content {
        padding: 12px
    }

    .stats-container {
        grid-template-columns: 1fr 1fr
    }

    .search-controls {
        grid-template-columns: 1fr
    }

    .data-table {
        font-size: .75rem
    }

    .data-table th,
    .data-table td {
        padding: 8px
    }
}

@media (max-width: 480px) {
    .header-content {
        flex-direction: column;
        gap: 15px
    }

    .stats-container {
        grid-template-columns: 1fr
    }

    .pagination-container {
        flex-direction: column;
        gap: 15px;
        align-items: stretch
    }

    .pagination-controls {
        justify-content: center
    }
}

/* ── Error Breakdown Panel ───────────────────────────────────── */
.error-breakdown-panel {
    margin: 18px 0 4px;
    background: #fff;
    border: 1px solid #fecaca;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(220, 38, 38, .07);
}

.error-breakdown-clear {
    padding: 14px 20px;
    color: #16a34a;
    font-weight: 600;
    font-size: .9rem;
    border-color: #bbf7d0;
    background: #f0fdf4;
    display: flex;
    align-items: center;
    gap: 8px;
}

.error-breakdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    background: linear-gradient(90deg, #fef2f2, #fff5f5);
    border-bottom: 1px solid #fecaca;
    flex-wrap: wrap;
    gap: 6px;
}

.eb-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.eb-export-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #fff;
    border: 1px solid #fca5a5;
    color: #b91c1c;
    font-size: .78rem;
    font-weight: 600;
    padding: 5px 11px;
    border-radius: 6px;
    cursor: pointer;
    transition: background .18s, border-color .18s;
}

.eb-export-btn:hover {
    background: #fef2f2;
    border-color: #ef4444;
}

.eb-toggle-btn {
    background: #fee2e2;
    border: 1px solid #fca5a5;
    color: #991b1b;
    font-size: .78rem;
    font-weight: 600;
    padding: 5px 11px;
    border-radius: 6px;
    cursor: pointer;
    transition: background .18s;
    white-space: nowrap;
}

.eb-toggle-btn:hover {
    background: #fecaca;
}

.eb-summary-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-left: 4px;
}

.eb-summary-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #fee2e2;
    border: 1px solid #fca5a5;
    color: #7f1d1d;
    font-size: .72rem;
    padding: 2px 8px;
    border-radius: 10px;
    max-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.eb-chip-count {
    background: #ef4444;
    color: #fff;
    font-size: .68rem;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 8px;
    flex-shrink: 0;
}

.eb-chip-more {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #6b7280;
}


.error-breakdown-title {
    font-weight: 700;
    font-size: .95rem;
    color: #991b1b;
    display: flex;
    align-items: center;
    gap: 6px;
}

.eb-icon {
    font-size: 1rem;
}

.error-breakdown-subtitle {
    font-size: .8rem;
    color: #b91c1c;
    opacity: .8;
}

.error-breakdown-body {
    padding: 8px 0;
}

.error-breakdown-row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 20px;
    transition: background .18s;
    border-bottom: 1px solid #fef2f2;
}

.error-breakdown-row:last-child {
    border-bottom: none;
}

.error-breakdown-row:hover {
    background: #fef2f2;
}

.eb-rank {
    font-size: .75rem;
    font-weight: 700;
    color: #b91c1c;
    min-width: 28px;
    text-align: center;
    background: #fee2e2;
    border-radius: 5px;
    padding: 3px 5px;
    flex-shrink: 0;
}

.eb-msg-block {
    flex: 1;
    min-width: 0;
}

.eb-message {
    font-size: .85rem;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.eb-hint {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    margin-top: 3px;
    margin-bottom: 2px;
    font-size: .78rem;
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 5px;
    padding: 3px 8px;
    line-height: 1.4;
}

.eb-hint-icon {
    flex-shrink: 0;
    font-size: .8rem;
}

.eb-codes {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 4px;
}

.eb-status-chip {
    font-size: .7rem;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 10px;
    display: inline-block;
}

.eb-bar-wrap {
    width: 140px;
    flex-shrink: 0;
    background: #fee2e2;
    border-radius: 6px;
    height: 8px;
    overflow: hidden;
}

.eb-bar {
    height: 100%;
    background: linear-gradient(90deg, #ef4444, #dc2626);
    border-radius: 6px;
    transition: width .4s ease;
    min-width: 2px;
}

.eb-count-block {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    min-width: 48px;
    flex-shrink: 0;
}

.eb-count {
    font-size: .95rem;
    font-weight: 700;
    color: #dc2626;
    line-height: 1.1;
}

.eb-pct {
    font-size: .72rem;
    color: #94a3b8;
}

/* ─────────────────────────────────────────────────────────────── */

.errors-dropdown {
    min-width: 320px;
    max-width: 480px;
}

.error-option-label {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #dc2626;
    font-size: .85rem;
    word-break: break-word;
}

.no-error-option-label {
    color: #16a34a;
    font-size: .85rem;
    font-weight: 600;
}

.error-dot {
    font-size: .6rem;
    flex-shrink: 0;
}

.manual-process-btn {
    background-color: #28a745;
    /* Green */
    color: white;
    border: none;
    padding: 5px 9px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;
}

.manual-process-btn:hover {
    background-color: #218838;
    /* Darker green on hover */
}

.manual-process-btn:active {
    transform: scale(0.97);
}

/* ─── IOT Status Badges ───────────────────────────────────────────────────── */
.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .3px;
}

.st-0 {
    background: rgba(39, 174, 96, .15);
    color: #065f46;
    border: 1px solid rgba(39, 174, 96, .3);
}

.st-1 {
    background: rgba(255, 193, 7, .2);
    color: #a16207;
    border: 1px solid rgba(255, 193, 7, .3);
}

.st-2 {
    background: rgba(231, 76, 60, .15);
    color: #991b1b;
    border: 1px solid rgba(231, 76, 60, .3);
}

.st-3 {
    background: rgba(155, 39, 176, .15);
    color: #6b21a8;
    border: 1px solid rgba(155, 39, 176, .3);
}

/* ─── IOT Row colours ─────────────────────────────────────────────────────── */
.row-warning {
    background: #fffbeb !important;
}

.row-failed {
    background: rgba(231, 76, 60, .04) !important;
    border-left: 3px solid #E74C3C;
}

.row-critical {
    background: rgba(155, 39, 176, .04) !important;
    border-left: 3px solid #9C27B0;
}

/* ─── Stats popup ─────────────────────────────────────────────────────────── */
.stats-popup {
    width: 900px;
    max-width: 95vw;
    max-height: 65vh;
    display: flex;
    flex-direction: column;
}

.stats-popup-body {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.stats-table-wrapper {
    flex: 1;
    overflow: auto;
    min-height: 0;
}

.stats-data-table {
    width: 100%;
    border-collapse: collapse;
}

.stats-data-table thead {
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    z-index: 10;
}

.stats-data-table th {
    padding: 12px 16px;
    text-align: left;
    color: white;
    font-weight: 600;
    font-size: .875rem;
    text-transform: uppercase;
    letter-spacing: .5px;
    white-space: nowrap;
}

.stats-data-table tbody tr {
    border-bottom: 1px solid #e2e8f0;
    background: #fff;
    cursor: pointer;
    transition: all .2s;
}

.stats-table-row:hover {
    background: #fff3e0 !important;
}

.stats-data-table td {
    padding: 12px 16px;
    font-size: .875rem;
    color: #334155;
}

/* ─── Misc overrides ─────────────────────────────────────────────────────── */
.stats-filter-note {
    font-size: .72rem;
    font-weight: 400;
    color: #94a3b8;
    font-style: italic;
    margin-left: 4px;
}

.filter-title-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.filter-icon {
    font-size: 1rem;
}

.filter-count-badge {
    background: #D2691E;
    color: white;
    border-radius: 12px;
    padding: 2px 8px;
    font-size: .7rem;
    font-weight: 700;
}

.desc-cell {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.loading-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: .82rem;
    color: #64748b;
}

/* ─── Slide-fade transition ──────────────────────────────────────────────── */
.slide-fade-enter-active {
    transition: all .25s ease;
}

.slide-fade-leave-active {
    transition: all .2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(-8px);
    opacity: 0;
}
</style>